import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import parse from "html-react-parser";
import FrontendLayout from "../../components/frontend/FrontendLayout";
import { handleApiError } from "../../utils/apiHelpers";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Blog {
  b_id: number;
  b_title: string;
  b_desc: string;
  b_pageurl: string;
  b_tag: string;
  b_postdt: string;
  b_img: string;
  b_status: number;
}

const BlogDetail: React.FC = () => {
  const { seourl } = useParams<{ seourl: string }>();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [recentBlogs, setRecentBlogs] = useState<Blog[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/blogs/frontend/view/${seourl}`
        );
        if (res.data.success && res.data.blog) {
          setBlog(res.data.blog);
        } else {
          toast.error("Blog not found.");
          navigate("/blog");
          return;
        }
      } catch (error) {
        handleApiError(error, navigate);
      }

      try {
        const resRecent = await axios.get(
          `${API_BASE}/api/blogs/frontend-list`
        );
        if (resRecent.data.success && Array.isArray(resRecent.data.blogs)) {
          const latest4 = resRecent.data.blogs
            .sort((a: Blog, b: Blog) => new Date(b.b_postdt).getTime() - new Date(a.b_postdt).getTime())
            .slice(0, 4);
          setRecentBlogs(latest4);
        }
      } catch (error) {
        console.error("Failed to fetch recent blogs", error);
      }
    };

    fetchBlog();
  }, [seourl, navigate]);

  if (!blog) return null;

  return (
    <FrontendLayout>
      <ToastContainer />

      <section
        className="flat-title style-2 mb-4 tf-spacing-1 inner-banner"
      >
        <div className="tf-container">
          <div className="row">
            <div className="col-lg-12">
              <div className="title-inner mb-0 pt-0 d-flex justify-content-between">
                <ul className="breadcrumb">
                  <li>
                    <Link to="/" className="home fw-6 text-color-3">
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link to="/blogs" className="home fw-6 text-color-3">
                      Blog
                    </Link>
                  </li>
                  <li>{blog.b_title}</li>
                </ul>
                <h5 className="mt-3">Blog Details</h5>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-blog-details">
        <div className="tf-container tf-spacing-1 pb-0">
          <div className="row">
            <div className="col-lg-8">
              <div className="heading">
                <h1 className="title-heading text-5">{blog.b_title}</h1>
                <div className="meta flex">
                  <div className="meta-item flex align-center">
                    <i className="bi bi-person"></i>
                    <p className="text-color-primary">Admin</p>
                  </div>
                  <div className="meta-item flex align-center">
                    <i className="bi bi-calendar-week"></i>
                    <p className="text-color-primary">
                      {new Date(blog.b_postdt).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                  {blog.b_tag && (
                    <div className="meta-item flex align-center">
                      <i className="bi bi-grid"></i>
                      <p>{blog.b_tag}</p>
                    </div>
                  )}
                </div>
              </div>

              {blog.b_img && (
                <div className="image-wrap mb-30">
                  <img
                    src={`${API_BASE}/uploads/blogs/${blog.b_img}`}
                    alt={blog.b_title}
                    className="lazyload"
                  />
                </div>
              )}

              <div className="wrap-content mb-20">
                {parse(blog.b_desc || "")}
              </div>

              <div className="tag-wrap flex justify-between items-center">
                <div className="tags">
                  <p>Tags:</p>
                  <div className="tags">
                    {blog.b_tag?.split(",").map((tag, idx) => (
                      <a href="#" key={idx}>
                        {tag.trim()}
                      </a>
                    ))}
                  </div>
                </div>
                <div className="wrap-social">
                  <p>Share this post:</p>
                  <ul className="tf-social style-1">
                    <li>
                      <a href="#">
                        <i className="bi bi-facebook"></i>
                      </a>
                    </li>
                    <li>
                      <a href="#">
                        <i className="bi bi-twitter-x"></i>
                      </a>
                    </li>
                    <li>
                      <a href="#">
                        <i className="bi bi-linkedin"></i>
                      </a>
                    </li>
                    <li>
                      <a href="#">
                        <i className="bi bi-instagram"></i>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="tf-sidebar">
                {/* Categories and Recent Posts can be dynamic later */}
                <div className="sidebar-item sidebar-categories">
                  <h4 className="sidebar-title">Categories</h4>
                  <ul className="list-categories">
                    <li className="flex items-center justify-between">
                      <a href="#" className="text-1 lh-20 fw-5">
                        Market Updates
                      </a>
                      <div className="number">(50)</div>
                    </li>
                    <li className="flex items-center justify-between">
                      <a href="#" className="text-1 lh-20 fw-5">
                        Buying Tips
                      </a>
                      <div className="number">(69)</div>
                    </li>
                    {/* Add more static or dynamic categories */}
                  </ul>
                </div>

                <div className="sidebar-item sidebar-featured pb-36">
                  <h4 className="sidebar-title">Recent Blogs</h4>
                  <ul>
                    {recentBlogs.map((recent) => (
                      <li className="box-listings hover-img" key={recent.b_id}>
                        <div className="image-wrap">
                          <Link to={`/blogs/${recent.b_pageurl}`}>
                            <img
                              src={`${API_BASE}/uploads/blogs/${recent.b_img}`}
                              alt={recent.b_title}
                              className="lazyload"
                            />
                          </Link>
                        </div>
                        <div className="content">
                          <div className="text-1 title fw-5">
                            <Link to={`/blogs/${recent.b_pageurl}`}>
                              {recent.b_title}
                            </Link>
                          </div>
                          <p>
                            <i className="bi bi-calendar"></i>{" "}
                            {new Date(recent.b_postdt).toLocaleDateString(
                              "en-GB",
                              {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              }
                            )}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </FrontendLayout>
  );
};

export default BlogDetail;
